import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Star, Phone, Car, Bike, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { getRide, cancelRide, rateRide, formatFcfa, formatDuration, RIDE_STATUS_LABELS, VEHICLES, NDJAMENA_CENTER } from "../../lib/ride";
import { toast } from "sonner";

const pin = (label, color, bg) => new L.DivIcon({
  className: "ride-pin",
  html: `<div style="background:${bg};color:${color};width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)"><span style="transform:rotate(45deg);font-weight:700;font-size:13px">${label}</span></div>`,
  iconSize: [30, 30], iconAnchor: [15, 30],
});
const pickupIcon = pin("A", "#0F0F0F", "#C8A04D");
const destIcon = pin("B", "#fff", "#B25E3F");
const driverIcon = pin("🚖", "#fff", "#0F0F0F");

const TONE_CLS = {
  amber: "bg-brand-gold/15 text-brand-goldDark border-brand-gold/40",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red: "bg-red-50 text-red-700 border-red-200",
  neutral: "bg-brand-sandlight text-brand-inkSoft border-brand-line",
};

export default function RideTrackPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  // Poll the ride every 5 s while non-terminal
  useEffect(() => {
    let timer = null;
    let alive = true;
    const tick = async () => {
      try {
        const r = await getRide(id);
        if (!alive) return;
        setRide(r);
        if (!["completed", "cancelled", "rejected"].includes(r.status)) {
          timer = setTimeout(tick, 5000);
        }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Impossible de charger la course");
      }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [id]);

  const handleCancel = async () => {
    if (!window.confirm("Annuler cette course ?")) return;
    setBusy(true);
    try {
      const updated = await cancelRide(id);
      setRide(updated);
      toast.success("Course annulée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const submitRating = async () => {
    if (!rating) return;
    setBusy(true);
    try {
      await rateRide(id, rating, comment);
      toast.success("Merci pour votre note !");
      navigate("/ride/history");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto px-5 py-12 text-center" data-testid="ride-track-error">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
        <p className="text-brand-ink">{error}</p>
        <Link to="/ride" className="text-brand-terracotta underline mt-3 inline-block">Retour</Link>
      </div>
    );
  }
  if (!ride) {
    return <div className="text-center py-16 text-brand-inkSoft">Chargement…</div>;
  }

  const status = RIDE_STATUS_LABELS[ride.status] || { label: ride.status, tone: "neutral" };
  const VehIcon = ride.vehicle_type === "moto" ? Bike : Car;
  const vehLabel = VEHICLES[ride.vehicle_type]?.label || ride.vehicle_type;
  const cancellable = ["requested", "accepted"].includes(ride.status);
  const showRating = ride.status === "completed" && !ride.client_rating;
  const driverPos = ride.driver?.current_lat ? [ride.driver.current_lat, ride.driver.current_lng] : null;

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-6" data-testid="ride-track">
      <Link to="/ride" className="text-xs text-brand-inkSoft hover:text-brand-ink">← Retour</Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 mt-2">
        <Card className="overflow-hidden rounded-2xl border-brand-line h-[55vh] lg:h-[78vh]" data-testid="ride-track-map">
          <MapContainer
            center={[ride.pickup.lat, ride.pickup.lng]}
            zoom={13}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[ride.pickup.lat, ride.pickup.lng]} icon={pickupIcon} />
            <Marker position={[ride.destination.lat, ride.destination.lng]} icon={destIcon} />
            {driverPos && <Marker position={driverPos} icon={driverIcon} />}
          </MapContainer>
        </Card>

        <div className="space-y-3">
          <div className={`rounded-2xl border px-4 py-3 inline-flex items-center gap-2 ${TONE_CLS[status.tone]}`} data-testid={`ride-status-${ride.status}`}>
            {ride.status === "requested" && <Loader2 className="w-4 h-4 animate-spin" />}
            {ride.status === "completed" && <CheckCircle2 className="w-4 h-4" />}
            <span className="font-semibold text-sm">{status.label}</span>
          </div>

          {/* Ride summary */}
          <Card className="rounded-2xl border-brand-line p-4">
            <div className="flex items-center gap-2 mb-3">
              <VehIcon className="w-5 h-5 text-brand-gold" />
              <span className="font-heading font-semibold text-brand-ink">{vehLabel}</span>
              <span className="ml-auto font-heading text-xl font-bold text-brand-ink">{formatFcfa(ride.final_price ?? ride.estimated_price)}</span>
            </div>
            <div className="mb-2">
              {ride.payment_method === "airtel" || ride.payment_method === "moov" ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-bold uppercase tracking-wider" data-testid="ride-payment-badge-paid">
                  ✓ Payé · {ride.payment_method === "airtel" ? "Airtel Money" : "Moov Money"}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-sandlight text-brand-inkSoft border border-brand-line text-[11px] font-bold uppercase tracking-wider" data-testid="ride-payment-badge-cash">
                  Espèces · à régler à l'arrivée
                </span>
              )}
            </div>
            <div className="text-xs space-y-1.5">
              <div className="flex gap-2">
                <span className="w-2 h-2 rounded-full bg-brand-gold mt-1.5 shrink-0" />
                <span className="text-brand-ink line-clamp-2">{ride.pickup.address || "Départ"}</span>
              </div>
              <div className="flex gap-2">
                <span className="w-2 h-2 rounded-full bg-brand-terracotta mt-1.5 shrink-0" />
                <span className="text-brand-ink line-clamp-2">{ride.destination.address || "Destination"}</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div className="bg-brand-sandlight rounded-xl px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">Distance</div><div className="font-semibold text-brand-ink">{ride.distance_km} km</div></div>
              <div className="bg-brand-sandlight rounded-xl px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-brand-inkMuted">Durée</div><div className="font-semibold text-brand-ink">{formatDuration(ride.duration_min)}</div></div>
            </div>
          </Card>

          {/* Driver info */}
          {ride.driver && (
            <Card className="rounded-2xl border-brand-line p-4" data-testid="ride-driver-info">
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold mb-2">Votre chauffeur</div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-heading font-semibold text-brand-ink">{ride.driver.name}</div>
                  <div className="text-xs text-brand-inkSoft">
                    {ride.driver.vehicle_color} · {ride.driver.vehicle_model || vehLabel}
                  </div>
                  <div className="text-sm font-bold text-brand-ink tracking-wider mt-0.5">{ride.driver.vehicle_plate}</div>
                </div>
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-brand-gold/15">
                  <Star className="w-3.5 h-3.5 fill-brand-gold text-brand-gold" />
                  <span className="text-sm font-bold text-brand-ink">{ride.driver.rating_avg.toFixed(1)}</span>
                </div>
              </div>
              {ride.driver.phone && (
                <a href={`tel:${ride.driver.phone}`} className="block mt-3" data-testid="ride-call-driver">
                  <Button variant="outline" className="w-full h-11 rounded-full border-brand-gold text-brand-goldDark hover:bg-brand-gold/10">
                    <Phone className="w-4 h-4 mr-2" /> Appeler le chauffeur
                  </Button>
                </a>
              )}
            </Card>
          )}

          {/* Actions */}
          {cancellable && (
            <Button onClick={handleCancel} disabled={busy} variant="outline" className="w-full h-11 rounded-full border-red-200 text-red-700 hover:bg-red-50" data-testid="ride-cancel">
              <X className="w-4 h-4 mr-1" /> Annuler la course
            </Button>
          )}

          {/* Rating */}
          {showRating && (
            <Card className="rounded-2xl border-brand-gold/40 bg-brand-gold/5 p-4" data-testid="ride-rating">
              <div className="font-heading font-semibold text-brand-ink mb-2">Notez votre chauffeur</div>
              <div className="flex gap-1.5 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    className="p-1"
                    data-testid={`ride-rate-star-${n}`}
                  >
                    <Star className={`w-7 h-7 ${rating >= n ? "fill-brand-gold text-brand-gold" : "text-brand-inkMuted"}`} />
                  </button>
                ))}
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Un commentaire (optionnel)"
                className="w-full text-sm rounded-xl border border-brand-line p-2.5 min-h-[68px]"
                rows={2}
              />
              <Button
                onClick={submitRating}
                disabled={!rating || busy}
                className="mt-2 w-full h-11 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white font-semibold"
                data-testid="ride-submit-rating"
              >
                Envoyer
              </Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
